import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export interface ColonneExport {
  cle: string;
  libelle: string;
  obligatoire: boolean;
}

export interface EtatSynchronisation {
  destination: string;
  operationnelle: boolean;
  derniereSynchro: string | null;
  derniereIssue: string | null;
  dernierMessage: string | null;
  lignesEnvoyees: number | null;
}

export interface FormationExport {
  code: string;
  titre: string;
  dossiers: number;
}

/** Valeur du filtre pour les dossiers dont la formation n'est pas arrêtée. */
export const SANS_FORMATION = 'SANS-FORMATION';

/** Valeur du filtre « toutes formations » : aucun paramètre n'est envoyé. */
export const TOUTES_FORMATIONS = '';

/**
 * Noms exacts des fichiers que produiront les routes de téléchargement. Ils
 * viennent de l'API : l'écran les annonce, il ne les recompose pas.
 */
export interface NomsFichiersExport {
  suiviCsv: string;
  suiviXlsx: string;
  sifaCsv: string;
  rqthXlsx: string;
}

/**
 * Volumes du tableau RQTH. `attestationManquante` compte les dossiers déclarés
 * sans attestation au dossier : hors fichier, mais annoncés à l'écran pour
 * qu'ils soient relancés.
 */
export interface VolumesRqth {
  dossiers: number;
  attestationManquante: number;
}

export interface ApercuExport {
  colonnes: ColonneExport[];
  colonnesDisponibles: ColonneExport[];
  lignes: Record<string, string>[];
  total: number;
  formations: FormationExport[];
  synchronisation: EtatSynchronisation;
  fichiers: NomsFichiersExport;
  rqth: VolumesRqth;
}

function parametres(site: string, colonnes: string[], formation: string): string {
  const query = new URLSearchParams({ site });
  if (colonnes.length > 0) query.set('colonnes', colonnes.join(','));
  if (formation) query.set('formation', formation);
  return query.toString();
}

export function useApercuExport(site: string, colonnes: string[], formation = TOUTES_FORMATIONS) {
  return useQuery({
    queryKey: ['export', site, colonnes.join(','), formation],
    queryFn: () => appelApi<ApercuExport>(`/export/suivi?${parametres(site, colonnes, formation)}`),
  });
}

/** URL de téléchargement : le navigateur porte le cookie de session lui-même. */
export function urlFichierExport(
  site: string,
  colonnes: string[],
  formation = TOUTES_FORMATIONS,
): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/export/suivi.csv?${parametres(site, colonnes, formation)}`;
}

/**
 * Export d'une sélection faite dans la liste des apprenants. Le périmètre de
 * sites reste la borne côté API : la sélection ne peut que le restreindre.
 */
export function urlExportSelection(site: string, dossiers: string[]): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  const query = new URLSearchParams({ site, dossiers: dossiers.join(',') });
  return `${base}/export/suivi.csv?${query.toString()}`;
}

/**
 * Classeur Excel au format du suivi tenu par TOP FORMATION : une feuille par
 * promotion. La sélection de colonnes ne s'y applique pas — la forme du
 * classeur est celle de leurs fichiers, elle ne se reconfigure pas.
 */
export function urlClasseurExport(site: string, formation = TOUTES_FORMATIONS): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  const query = new URLSearchParams({ site });
  if (formation) query.set('formation', formation);
  return `${base}/export/suivi.xlsx?${query.toString()}`;
}

export interface ResultatSynchronisation {
  issue: string;
  message: string | null;
  nombreLignes: number;
  destination: string;
}

export function useSynchroniserExport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { site: string; colonnes: string[]; formation?: string }) =>
      appelApi<ResultatSynchronisation>('/export/synchronisation', {
        methode: 'POST',
        corps: variables,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['export'] });
    },
  });
}

/**
 * Apprenants ayant une attestation RQTH, au format du classeur Excel tenu par
 * TOP FORMATION. Donnée de santé : ce fichier ne part jamais dans la
 * synchronisation partagée, et l'API trace chaque téléchargement dossier par
 * dossier.
 */
export function urlExportRqth(site: string, formation = TOUTES_FORMATIONS): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  const query = new URLSearchParams({ site });
  if (formation) query.set('formation', formation);
  return `${base}/export/rqth.xlsx?${query.toString()}`;
}
